import { IconCancel, IconCheck, IconX } from '@u21/tabler-icons';
import styled, { css, keyframes } from 'styled-components';
import {
  U21_PULSE_INNER_ANIMATION,
  U21PulseIndicator,
} from 'app/shared/u21-ui/components/display/U21PulseIndicator';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21AiChatMessageToolUse } from 'app/shared/u21-ui/components/input/chat/types';
import { formatDatetime } from 'app/shared/utils/date';

type BlurbColor = 'error' | 'text.primary' | 'text.secondary';

const getBlurbColor = (
  status: U21AiChatMessageToolUse['status'],
): BlurbColor => {
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'pending' || status === 'running' || status === 'cancelled') {
    return 'text.secondary';
  }
  return 'text.primary';
};

interface Props {
  toolUse: U21AiChatMessageToolUse;
}

export const U21AiChatToolCallInner = ({ toolUse }: Props) => {
  const { blurb, createdAt, errorMessage, icon: Icon, status } = toolUse;
  const timestamp = createdAt ? formatDatetime(createdAt) : undefined;
  const isInProgress = status === 'pending' || status === 'running';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';

  return (
    <Row align="start" horizontal spacing={1} useFlexGap>
      <IconBox $isCancelled={isCancelled}>
        <Icon color="currentColor" />
      </IconBox>
      <TextColumn spacing={0.5}>
        <BlurbText
          $isPending={isInProgress}
          breakWord
          color={getBlurbColor(status)}
          variant="body2"
        >
          {blurb}
        </BlurbText>
        {isFailed && errorMessage && (
          <U21Typography breakWord color="error" variant="caption">
            {errorMessage}
          </U21Typography>
        )}
        {timestamp && (
          <U21Typography color="text.secondary" variant="caption">
            {timestamp}
          </U21Typography>
        )}
      </TextColumn>
      <StatusColumn>
        {isInProgress && (
          <PulseWrap>
            <U21PulseIndicator color="primary" size="small" />
          </PulseWrap>
        )}
        {status === 'success' && (
          <SuccessIcon aria-hidden color="currentColor" />
        )}
        {isFailed && <FailedIcon aria-hidden color="currentColor" />}
        {isCancelled && (
          <CancelledIcon
            aria-label="Cancelled"
            color="currentColor"
            role="img"
          />
        )}
      </StatusColumn>
    </Row>
  );
};

const pendingTextPulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const BlurbText = styled(U21Typography)<{ $isPending: boolean }>`
  ${({ $isPending }) =>
    $isPending
      ? css`
          animation: ${pendingTextPulse} ${U21_PULSE_INNER_ANIMATION};
        `
      : css``}
`;

const Row = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const IconBox = styled.div<{ $isCancelled?: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.background.neutral};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  color: ${({ $isCancelled, theme }) =>
    $isCancelled ? theme.palette.text.disabled : theme.palette.text.secondary};
  display: flex;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing(4)};
  justify-content: center;
  width: ${({ theme }) => theme.spacing(4)};
`;

const TextColumn = styled(U21Spacer)`
  flex: 1;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const StatusColumn = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  display: flex;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing(4)};
  justify-content: center;
  overflow: hidden;
  width: ${({ theme }) => theme.spacing(3)};
`;

const PulseWrap = styled.div`
  align-items: center;
  display: flex;
  height: ${({ theme }) => theme.spacing(3)};
  justify-content: center;
  overflow: hidden;
  width: ${({ theme }) => theme.spacing(3)};
`;

const SuccessIcon = styled(IconCheck)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const FailedIcon = styled(IconX)`
  color: ${({ theme }) => theme.palette.error.main};
`;

const CancelledIcon = styled(IconCancel)`
  color: ${({ theme }) => theme.palette.text.disabled};
`;
